export type PickListSearchable = {
  title: string;
  subtitle?: string;
  /** Extra haystack text that is not shown (optional). */
  searchText?: string;
  /** Localized dataset source; used by the dataset filter and combined search. */
  datasetLabel?: string;
};

/** Unique dataset labels in first-seen order (skips items without a label). */
export function uniquePickListDatasets(
  items: readonly { datasetLabel?: string }[],
): string[] {
  const labels: string[] = [];
  const seen = new Set<string>();
  for (const item of items) {
    const label = item.datasetLabel;
    if (!label || seen.has(label)) continue;
    seen.add(label);
    labels.push(label);
  }
  return labels;
}

/**
 * Whitespace-split AND filter: every token must be a case-insensitive
 * substring of title + subtitle + optional searchText. Empty query keeps all.
 * When `datasetLabel` is set, only items from that dataset remain.
 */
export function filterPickListItems<T extends PickListSearchable>(
  items: readonly T[],
  query: string,
  datasetLabel?: string,
): T[] {
  const scoped = datasetLabel
    ? items.filter((item) => item.datasetLabel === datasetLabel)
    : items;

  const tokens = query
    .trim()
    .split(/\s+/)
    .filter((token) => token.length > 0);
  if (tokens.length === 0) return [...scoped];

  return scoped.filter((item) => {
    const haystack = [item.title, item.subtitle, item.searchText]
      .filter((part): part is string => !!part)
      .join(" ")
      .toLowerCase();
    return tokens.every((token) => haystack.includes(token.toLowerCase()));
  });
}
